import { Dispatch, RefObject, SetStateAction } from 'react';

import { useParams } from 'react-router';

import { FormikHelpers } from 'formik';
import { enqueueSnackbar } from 'notistack';

import { useShowError } from 'hooks/useShowError';
import { useFormMutation } from 'mutations/useFormMutation';
import { useConfigPedidoStepContext } from 'pages/compra/configuracao-pedido/contexts';
import { ConfigPedidoQuebrasDinamicas, ProdutoQuebra } from 'pages/compra/configuracao-pedido/types';

interface UseMutateQuebrasDinamicas {
    initialData: ConfigPedidoQuebrasDinamicas;
    setOpenQuebrasModal: Dispatch<SetStateAction<boolean>>;
    messageRef: RefObject<string>;
}

/**
 * Valida se o produto possui porcentagem e condição de pagamento.
 */
const validateProduct = (produtoQuebraList: ProdutoQuebra[]) => {
    const productIncorrect = produtoQuebraList.find((item) => !item.condicaoPagamento || !item.peQuebra);

    if (productIncorrect) {
        const { peQuebra, nrQuebra, condicaoPagamento, produto } = productIncorrect;
        const { dsProduto } = produto || {};

        let message = '';

        if (!peQuebra) {
            message = `O produto ${dsProduto} com a quebra de N.º ${nrQuebra} não contém porcentagem`;
        } else if (!condicaoPagamento) {
            message = `O produto ${dsProduto} com a quebra de N.º ${nrQuebra} não contém condição de pagamento`;
        }

        enqueueSnackbar(message, { variant: 'error' });

        return false;
    }

    return true;
};

/**
 * Valida se a porcentagem total do produto é igual a 100%.
 */
const validateProductPercentage = (produtoQuebraList: ProdutoQuebra[]) => {
    const totals: { idProduto: number; dsProduto: string; peQuebra: number }[] = [];

    produtoQuebraList.forEach((p) => {
        const existing = totals.find((t) => t.idProduto === p.produto?.idProduto);

        if (!existing) {
            totals.push({ idProduto: p.produto.idProduto, peQuebra: p.peQuebra || 0, dsProduto: p.produto.dsProduto });
        } else {
            const idx = totals.findIndex((t) => t.idProduto === p.produto?.idProduto);

            totals.splice(idx, 1, { ...existing, peQuebra: existing.peQuebra + (p.peQuebra || 0) });
        }
    });

    const incorrect = totals.find((t) => t.peQuebra !== 100);

    if (incorrect) {
        enqueueSnackbar(
            `O produto ${incorrect.dsProduto} tem uma porcentagem total de quebra de ${incorrect.peQuebra}%. A soma das porcentagens de quebra deve ser igual a 100%`,
            { variant: 'error' },
        );

        return false;
    }

    return true;
};

export function useMutateQuebrasDinamicas({ initialData, setOpenQuebrasModal, messageRef }: UseMutateQuebrasDinamicas) {
    const { setActiveStepSaved } = useConfigPedidoStepContext();
    const { mutate, isLoading } = useFormMutation();
    const { showError } = useShowError();
    const { id } = useParams();

    /**
     * Retorna a lista de produtos com as quebras dinâmicas.
     */
    const getProdutosQuebrasDinamicas = (produtoQuebraList: ProdutoQuebra[]) => {
        const newList = [...produtoQuebraList];

        const removedProducts = initialData.configPedidoQuebrasDinamicas
            .filter((produto) => !produtoQuebraList.some((item) => item.produto.idProduto === produto.produto.idProduto))
            .map((produto) => ({
                ...produto,
                fgAtivo: false,
            }));

        return [...newList, ...removedProducts].map((produto) => ({
            ...produto,
            dtInicio: produto.dtInicio ? `${String(produto.dtInicio).substring(0, 10)}T00:00:00Z` : produto.dtInicio,
            dtFim: produto.dtFim ? `${String(produto.dtFim).substring(0, 10)}T00:00:00Z` : produto.dtFim,
            configPedido: { idConfigPedido: Number(id) },
        }));
    };

    const onSubmit = (formData: ConfigPedidoQuebrasDinamicas, { resetForm }: FormikHelpers<ConfigPedidoQuebrasDinamicas>) => {
        if (validateProduct(formData.configPedidoQuebrasDinamicas) && validateProductPercentage(formData.configPedidoQuebrasDinamicas)) {
            mutate(
                {
                    url: '/gestao/configpedidoquebra/insertlist',
                    formData: getProdutosQuebrasDinamicas(formData.configPedidoQuebrasDinamicas),
                    preventSnack: true,
                },
                {
                    onSuccess: ({ data }: any) => {
                        enqueueSnackbar('Configuração de quebra salva com sucesso', { variant: 'success' });

                        resetForm({ values: { configPedidoQuebrasDinamicas: data?.data } });

                        setActiveStepSaved();

                        if (data.message) {
                            setOpenQuebrasModal(true);

                            messageRef.current = data.message;
                        }
                    },
                    onError: (error: any) => showError(error, 'Não foi possível salvar a configuração de quebra'),
                },
            );
        }
    };

    return { onSubmit, isLoading };
}
